import React from "react";
import { FileText, LayoutDashboard, FileCheck2, ChevronRight, AlertTriangle, Sparkles } from "lucide-react";
import { useVisualCopy } from "@/locales/visualCopy";

const FRAME = "card-surface overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)] min-w-0 w-full";
const CHROME = "flex items-center justify-between px-4 py-2.5 border-b border-[var(--td-border)] bg-[var(--td-surface-2)]";
const DOT = "w-2.5 h-2.5 rounded-full bg-[#3a3f4c]";

function Chrome({ title }) {
  return (
    <div className={CHROME}>
      <div className="flex items-center gap-1.5">
        <div className={DOT} /><div className={DOT} /><div className={DOT} />
      </div>
      <div className="font-mono text-[8px] sm:text-[10px] text-[var(--td-text-3)] tracking-wider sm:tracking-widest truncate px-2">{title}</div>
      <div className="w-12" />
    </div>
  );
}

/* --------- Scene 1: Dashboard --------- */
export function DashboardScene() {
  const v = useVisualCopy();
  const deals = [
    [v("oilAfrica"), "ABC Traders", v("stageDrafting"), "gold"],
    [v("steelItaly"), "Aksoy Metal AŞ", v("stageQa"), "blue"],
    [v("wheatSea"), "Meridian Grain", v("stageIntake"), "muted"],
    [v("fuelMed"), "Baltika Shipping", v("stageDelivery"), "emerald"],
  ];
  const stageColor = { gold: "text-[var(--td-gold)] border-[var(--td-gold)]", blue: "text-blue-300 border-blue-400", muted: "text-[var(--td-text-3)] border-[var(--td-border)]", emerald: "text-emerald-300 border-emerald-400" };
  return (
    <div className={FRAME}>
      <Chrome title={`TRADEDOCS · ${v("dashboard")}`} />
      <div className="p-3 sm:p-5 bg-[var(--td-bg)] min-h-[320px] sm:min-h-[360px] min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--td-gold)] mb-1">{v("workspace")}</div>
            <div className="font-serif text-2xl">{v("activeDeals")}</div>
          </div>
          <div className="btn-gold text-[10px] font-mono uppercase tracking-widest !py-1.5 !px-3">{v("newDeal")}</div>
        </div>
        <div className="border border-[var(--td-border)]">
          <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)] sm:grid-cols-[2fr_1.5fr_1fr_0.6fr] px-3 sm:px-4 py-2 border-b border-[var(--td-border)] font-mono text-[8px] sm:text-[9px] uppercase tracking-wider sm:tracking-widest text-[var(--td-text-2)]">
            <span>{v("title")}</span><span>{v("counterparty")}</span><span className="hidden sm:block">{v("stage")}</span><span className="hidden sm:block">4 {v("drafts")}</span>
          </div>
          {deals.map(([t, c, s, col], i) => (
            <div key={i} className="grid grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)] sm:grid-cols-[2fr_1.5fr_1fr_0.6fr] px-3 sm:px-4 py-2.5 items-center border-b border-[var(--td-border)] last:border-b-0 text-[9px] sm:text-[11px] hover:bg-[var(--td-surface-2)] min-w-0">
              <span className="font-serif text-sm text-[var(--td-text)] truncate">{t}</span>
              <span className="text-[var(--td-text-2)] font-mono truncate">{c}</span>
              <span className="hidden sm:block"><span className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${stageColor[col]}`}>{s}</span></span>
              <span className="hidden sm:block font-mono text-[9px] text-[var(--td-text-3)]">v{i + 1}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* --------- Scene 2: Template Library --------- */
export function TemplateLibraryScene() {
  const v = useVisualCopy();
  const templates = [
    ["LOI", v("loi"), v("commercial")], ["ICPO", v("icpo"), v("commercial")],
    ["FCO", v("fco"), v("commercial")], ["LC", v("lc"), v("tradeFinance")],
    ["BCL", v("bcl"), v("tradeFinance")], ["SBLC", v("sblc"), v("tradeFinance")],
    ["Recap", v("recap"), v("shipping")], ["Laytime", v("laytime"), v("shipping")], ["DDQ", v("ddq"), v("corporate")],
  ];
  return (
    <div className={FRAME}>
      <Chrome title={`TRADEDOCS · ${v("templateLibrary")}`} />
      <div className="p-3 sm:p-5 bg-[var(--td-bg)] min-h-[320px] sm:min-h-[360px] min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--td-gold)] mb-1">{v("library")}</div>
            <div className="font-serif text-2xl">{v("templates17")}</div>
          </div>
          <div className="border border-[var(--td-border)] px-3 py-1.5 font-mono text-[10px] text-[var(--td-text-3)]">{v("search")}</div>
        </div>
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {[v("all"), v("commercial"), v("tradeFinance"), v("shipping"), v("corporate")].map((c, i) => (
            <div key={c} className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border shrink-0 ${i === 0 ? "border-[var(--td-gold)] text-[var(--td-gold)]" : "border-[var(--td-border)] text-[var(--td-text-2)]"}`}>
              {c}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 min-[390px]:grid-cols-2 sm:grid-cols-3 gap-2">
          {templates.map(([code, title, cat]) => (
            <div key={code} className="border border-[var(--td-border)] p-3 hover:border-[var(--td-gold)] cursor-pointer">
              <div className="flex items-start justify-between mb-1">
                <span className="font-mono text-[10px] text-[var(--td-gold)]">{code}</span>
                <span className="font-mono text-[8px] uppercase tracking-widest text-[var(--td-text-3)]">{cat}</span>
              </div>
              <div className="font-serif text-xs text-[var(--td-text)] leading-tight">{title}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* --------- Scene 3: Document Review --------- */
export function DocumentReviewScene() {
  const v = useVisualCopy();
  return (
    <div className={FRAME}>
      <Chrome title={`TRADEDOCS · ${v("documentReview")}`} />
      <div className="grid md:grid-cols-[minmax(0,1fr)_260px] min-h-[360px] min-w-0">
        <div className="p-3 sm:p-5 bg-[var(--td-bg)] min-w-0 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--td-gold)] mb-1">FCO · v3</div>
              <div className="font-serif text-xl">{v("fco")}</div>
            </div>
            <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-amber-300">
              <AlertTriangle size={11} /> {v("issues")}
            </div>
          </div>
          <div className="font-mono text-[10.5px] leading-relaxed text-[var(--td-text-2)] space-y-1.5">
            <div className="text-[var(--td-text)]"><span className="text-[var(--td-gold)]">###</span> 4) {v("price")}</div>
            <div className="bg-amber-500/10 border-l-2 border-amber-400 pl-2 py-0.5 text-[var(--td-text)]">
              USD 985 / MT <span className="text-amber-300">◄ {v("priceIssue")}</span>
            </div>
            <div className="h-2" />
            <div className="text-[var(--td-text)]"><span className="text-[var(--td-gold)]">###</span> 5) {v("paymentTerms")}</div>
            <div>LC MT700</div>
            <div className="bg-red-500/10 border-l-2 border-red-400 pl-2 py-0.5">
              {v("issuingBank")} <span className="text-red-300">◄ {v("missingBank")}</span>
            </div>
            <div className="h-2" />
            <div className="text-[var(--td-text)]"><span className="text-[var(--td-gold)]">###</span> 6) {v("delivery")}</div>
            <div className="bg-amber-500/10 border-l-2 border-amber-400 pl-2 py-0.5 text-[var(--td-text)]">
              CIF Mombasa 2020 <span className="text-amber-300">◄ {v("yearFormat")}</span>
            </div>
          </div>
        </div>
        <div className="border-t md:border-t-0 md:border-l border-[var(--td-border)] p-4 bg-[var(--td-surface)]">
          <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--td-text-3)] mb-3">{v("suggestions")}</div>
          <div className="space-y-3">
            {[
              [v("priceBasis"), v("priceSuggestion")], [v("issuingBank"), v("bankSuggestion")], [v("incoterms"), v("yearSuggestion")],
            ].map(([k, v], i) => (
              <div key={i} className="border border-[var(--td-border)] p-2">
                <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--td-gold)] mb-0.5">{k}</div>
                <div className="text-[10px] text-[var(--td-text-2)] leading-tight">{v}</div>
              </div>
            ))}
            <div className="pt-2 flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-[var(--td-gold)]">
              <Sparkles size={10} /> {v("aiReview")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------- Compact scene rail --------- */
export default function MockupScenes() {
  const v = useVisualCopy();
  const scenes = [
    { Icon: LayoutDashboard, label: v("tabDashboard"), Cmp: DashboardScene },
    { Icon: FileText, label: v("tabTemplates"), Cmp: TemplateLibraryScene },
    { Icon: FileCheck2, label: v("tabReview"), Cmp: DocumentReviewScene },
  ];
  const [active, setActive] = React.useState(0);
  const Active = scenes[active].Cmp;
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6 justify-center">
        {scenes.map((s, i) => (
          <button
            key={s.label}
            onClick={() => setActive(i)}
            data-testid={`preview-tab-${i}`}
            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors inline-flex items-center gap-2 ${
              active === i
                ? "border-[var(--td-gold)] text-[var(--td-gold)] bg-[var(--td-gold-muted)]"
                : "border-[var(--td-border)] text-[var(--td-text-2)] hover:border-[var(--td-text-2)]"
            }`}
          >
            <s.Icon size={12} /> {s.label}
          </button>
        ))}
      </div>
      <Active />
    </div>
  );
}
